import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { VersionStatus } from '@katsquare/types';
import { Button, Spinner } from '../components/ui';
import {
  deleteVersion,
  getIssue,
  getProject,
  getProof,
  renameProof,
  uploadVersion,
  versionOriginalUrl,
} from '../lib/api';
import { useCanEdit, useWorkspaceRole } from '../features/auth/useWorkspaceRole';
import { useToast } from '../components/Toast';
import { useFileDrop } from '../lib/useFileDrop';
import { showUploadError } from '../lib/uploadError';
import { usePlanLimit } from '../components/PlanLimitDialog';
import { StatusBadge, VersionPane } from '../features/proofs/VersionPane';
import { CompareView } from '../features/proofs/CompareView';
import { ReviewControls } from '../features/proofs/ReviewBar';
import { ShareDialog } from '../features/proofs/ShareDialog';
import type { DrawTool } from '../features/proofs/ProofViewer';
import { IconCompare, IconDownload, IconTag, IconUpload } from '../components/icons';
import { PdfTour } from '../components/PdfTour';

export function ProofReviewPage() {
  const { proofId = '' } = useParams();
  const queryClient = useQueryClient();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  // Text selection is the default tool so reviewers can read/quote right away.
  const [tool, setTool] = useState<DrawTool | null>('text');
  const [comparing, setComparing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const proof = useQuery({
    queryKey: ['proof', proofId],
    queryFn: () => getProof(proofId),
    // Poll so a reviewer viewing an older round notices a freshly-uploaded one.
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });
  // Newer-version banner: dismissal is keyed by the latest version id, so a
  // still-newer upload re-triggers it but re-dismissing the same one doesn't.
  const [dismissedVersionId, setDismissedVersionId] = useState<string | null>(null);
  const issue = useQuery({
    queryKey: ['issue', proof.data?.issueId],
    queryFn: () => getIssue(proof.data!.issueId),
    enabled: !!proof.data?.issueId,
  });
  const projectQuery = useQuery({
    queryKey: ['project', issue.data?.projectId],
    queryFn: () => getProject(issue.data!.projectId),
    enabled: !!issue.data?.projectId,
  });
  const canEdit = useCanEdit(projectQuery.data?.workspaceId);
  // Anyone on the proof can hand it to a client — REVIEWERs included, who are
  // often the ones actually chasing the sign-off. Looser than canEdit, not
  // stricter. The real gate is the Business plan, enforced by the API; the
  // dialog turns into an upgrade prompt when the account isn't on it.
  const role = useWorkspaceRole(projectQuery.data?.workspaceId);
  const canShare = role !== undefined;
  const [sharing, setSharing] = useState(false);

  const [searchParams] = useSearchParams();
  const versionParam = searchParams.get('v');

  // Keep the current selection only if it belongs to the proof now loaded;
  // otherwise (first load, or after jumping to another PDF) re-derive it from
  // the ?v= param or the latest round. Without the membership check a stale id
  // from the previous PDF would stick and hide the version until a refresh.
  useEffect(() => {
    if (!proof.data) return;
    if (selectedVersionId && proof.data.versions.some((v) => v.id === selectedVersionId)) return;
    const wanted = proof.data.versions.find((v) => v.id === versionParam);
    setSelectedVersionId(wanted?.id ?? proof.data.latestVersion?.id ?? null);
  }, [proof.data, selectedVersionId, versionParam]);

  // Leaving compare when switching PDFs avoids a flash of the wrong comparison.
  useEffect(() => setComparing(false), [proofId]);

  const selectVersion = (id: string) => {
    setSelectedVersionId(id);
    setComparing(false);
  };
  const selectTool = (t: DrawTool) => setTool((prev) => (prev === t ? null : t));

  const toast = useToast();
  const navigate = useNavigate();
  const showPlanLimit = usePlanLimit();
  const newVersion = useMutation({
    mutationFn: (file: File) => uploadVersion(proofId, file),
    onSuccess: (p) => {
      // Seed the cache with the proof the upload returned *before* selecting the
      // new round: otherwise the selection effect below sees a version id that
      // isn't in the (still stale) list yet, resets it to the old latest, and the
      // uploader is left staring at "a newer version is available" — their own.
      queryClient.setQueryData(['proof', proofId], p);
      if (p.latestVersion) selectVersion(p.latestVersion.id);
      queryClient.invalidateQueries({ queryKey: ['proof', proofId] });
      // The board rows (thumbnail, round number, status, open-comment badge) all
      // describe the *latest* version, so a new round makes them stale — and the
      // sidebar's copy has no poll to correct itself.
      queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
    },
    onError: (e) => showUploadError(e, { toast, showPlanLimit, onUpgrade: () => navigate('/pricing') }),
  });
  const removeVersion = useMutation({
    mutationFn: (id: string) => deleteVersion(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['proof', proofId] });
      // Deleting the newest round moves "latest" back a version — same staleness.
      queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
      if (id === selectedVersionId) {
        const next = (proof.data?.versions ?? [])
          .filter((v) => v.id !== id)
          .sort((a, b) => b.number - a.number)[0];
        if (next) selectVersion(next.id);
      }
      toast('Version deleted');
    },
    onError: (e: Error) => toast(e.message),
  });

  const versions = proof.data?.versions ?? [];
  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const previousVersion = selectedVersion
    ? versions.filter((v) => v.number < selectedVersion.number).sort((a, b) => b.number - a.number)[0]
    : undefined;
  const projectId = issue.data?.projectId;
  const { isOver, dropHandlers } = useFileDrop((files) => {
    if (files[0]) newVersion.mutate(files[0]);
  });

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      {/* First-time on-page walkthrough of the review tools (once the viewer is up). */}
      <PdfTour enabled={!comparing && selectedVersion?.status === 'READY'} />
      {/* One row on phones (icon-only back, actions behind ⋯); the full spread
          from md up, where the tour and the desktop controls live. */}
      <header className="flex min-w-0 flex-nowrap items-center gap-x-2 gap-y-2 border-b border-neutral-200 bg-white px-3 py-2 md:flex-wrap md:gap-x-3 md:px-5">
        <Link
          to={projectId ? `/projects/${projectId}` : '/'}
          title={issue.data?.name ?? 'Back'}
          className="shrink-0 text-sm text-neutral-500 hover:text-neutral-900"
        >
          ← <span className="hidden md:inline">{issue.data?.name ?? 'Back'}</span>
        </Link>
        <span className="hidden text-neutral-300 md:inline">/</span>
        <ProofName proofId={proofId} name={proof.data?.name ?? 'Proof'} editable={canEdit} />

        {versions.length > 0 && selectedVersion && (
          <VersionDropdown
            versions={versions}
            selectedId={selectedVersionId}
            selected={selectedVersion}
            onSelect={selectVersion}
            onDelete={canEdit && versions.length > 1 ? (id) => removeVersion.mutate(id) : undefined}
          />
        )}

        {/* Kept outside the desktop-only group so the ⋯ menu can trigger it too. */}
        {canEdit && (
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) newVersion.mutate(f);
              e.target.value = '';
            }}
          />
        )}

        <MobileActions
          proofId={proofId}
          versionId={selectedVersionId}
          comparing={comparing}
          canCompare={!!previousVersion}
          onToggleCompare={() => setComparing((c) => !c)}
          canEdit={canEdit}
          uploading={newVersion.isPending}
          onUpload={() => fileRef.current?.click()}
          canShare={canShare}
          onShare={() => setSharing(true)}
        />

        <div className="ml-auto hidden flex-wrap items-center gap-2 md:flex">
          {selectedVersionId && !comparing && (
            <span data-tour="pdf-review" className="flex items-center gap-2">
              <ReviewControls versionId={selectedVersionId} proofId={proofId} />
            </span>
          )}
          {previousVersion && (
            <span data-tour="pdf-compare" className="flex">
              <Button
                variant={comparing ? 'primary' : 'ghost'}
                onClick={() => setComparing((c) => !c)}
                className={`flex items-center gap-1.5 border px-3 py-1.5 text-sm ${
                  comparing ? 'border-neutral-900' : 'border-neutral-200'
                }`}
                title="Compare with the previous version"
              >
                <IconCompare className="h-4 w-4" />
                {comparing ? 'Exit compare' : 'Compare'}
              </Button>
            </span>
          )}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-neutral-200" />
          {canShare && (
            <Button
              variant="ghost"
              onClick={() => setSharing(true)}
              title="Share with clients who don't have an account"
              className="flex shrink-0 items-center gap-1.5 border border-neutral-200 px-3 py-1.5 text-sm"
            >
              <IconTag className="h-4 w-4" />
              Share
            </Button>
          )}
          {selectedVersionId && (
            <a
              href={versionOriginalUrl(selectedVersionId)}
              download
              title="Download the original PDF"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
            >
              <IconDownload />
            </a>
          )}
          {canEdit && (
            <span data-tour="pdf-newversion" className="flex">
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={newVersion.isPending}
                title="Upload a new version"
                className="flex shrink-0 items-center gap-1.5 px-3 py-1.5 text-sm"
              >
                {newVersion.isPending ? <Spinner /> : <IconUpload className="h-4 w-4" />}
                New version
              </Button>
            </span>
          )}
        </div>
      </header>

      {(() => {
        const latest = proof.data?.latestVersion;
        const show =
          !comparing &&
          !!latest &&
          !!selectedVersion &&
          latest.number > selectedVersion.number &&
          dismissedVersionId !== latest.id;
        if (!show || !latest) return null;
        return (
          <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-800">
            <span className="flex-1">A newer version (v{latest.number}) is available.</span>
            <button
              type="button"
              onClick={() => selectVersion(latest.id)}
              className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
            >
              View v{latest.number}
            </button>
            <button
              type="button"
              onClick={() => setDismissedVersionId(latest.id)}
              className="rounded-md px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
            >
              Dismiss
            </button>
          </div>
        );
      })()}

      <div className="relative min-h-0 flex-1" {...dropHandlers}>
        {isOver && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-blue-50/85 text-base font-medium text-blue-700">
            Drop to upload a new version
          </div>
        )}
        {proof.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : comparing && previousVersion && selectedVersion ? (
          <CompareView
            versions={versions}
            initialLeftId={previousVersion.id}
            initialRightId={selectedVersion.id}
          />
        ) : selectedVersionId ? (
          <VersionPane
            key={selectedVersionId}
            versionId={selectedVersionId}
            tool={tool}
            onSelectTool={selectTool}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-neutral-400">
            This PDF has no versions yet.
          </div>
        )}
      </div>

      {sharing && <ShareDialog proofId={proofId} onClose={() => setSharing(false)} />}
    </div>
  );
}


/**
 * Phone-sized header actions: everything the desktop header spreads out (review
 * gate, compare, share, download, new version) folded into one ⋯ menu, so the
 * header stays a single row. Anything added to the desktop group belongs here
 * too, or it simply doesn't exist on a phone.
 */
function MobileActions({
  proofId,
  versionId,
  comparing,
  canCompare,
  onToggleCompare,
  canEdit,
  uploading,
  onUpload,
  canShare,
  onShare,
}: {
  proofId: string;
  versionId: string | null;
  comparing: boolean;
  canCompare: boolean;
  onToggleCompare: () => void;
  canEdit: boolean;
  uploading: boolean;
  onUpload: () => void;
  canShare: boolean;
  onShare: () => void;
}) {
  const [open, setOpen] = useState(false);
  const item =
    'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100';

  return (
    <div className="relative ml-auto shrink-0 md:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="More actions"
        aria-label="More actions"
        aria-expanded={open}
        className="flex h-8 w-8 items-center justify-center rounded-md text-lg leading-none text-neutral-600 hover:bg-neutral-100"
      >
        ⋯
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-60 rounded-lg border border-neutral-200 bg-white p-1.5 shadow-lg">
            {versionId && !comparing && (
              <div className="flex flex-wrap items-center gap-2 border-b border-neutral-100 px-2 pb-2 pt-1">
                <ReviewControls versionId={versionId} proofId={proofId} />
              </div>
            )}
            {canCompare && (
              <button
                type="button"
                className={item}
                onClick={() => {
                  onToggleCompare();
                  setOpen(false);
                }}
              >
                <IconCompare className="h-4 w-4 text-neutral-500" />
                {comparing ? 'Exit compare' : 'Compare with previous'}
              </button>
            )}
            {canShare && (
              <button
                type="button"
                className={item}
                onClick={() => {
                  onShare();
                  setOpen(false);
                }}
              >
                <IconTag className="h-4 w-4 text-neutral-500" />
                Share with clients
              </button>
            )}
            {versionId && (
              <a
                href={versionOriginalUrl(versionId)}
                download
                className={item}
                onClick={() => setOpen(false)}
              >
                <IconDownload />
                Download original
              </a>
            )}
            {canEdit && (
              <button
                type="button"
                className={item}
                disabled={uploading}
                onClick={() => {
                  onUpload();
                  setOpen(false);
                }}
              >
                {uploading ? <Spinner /> : <IconUpload className="h-4 w-4 text-neutral-500" />}
                New version
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type VersionLite = { id: string; number: number; status: VersionStatus };

/** Filestage-style version switcher: a compact dropdown (stable header width). */
function VersionDropdown({
  versions,
  selectedId,
  selected,
  onSelect,
  onDelete,
}: {
  versions: VersionLite[];
  selectedId: string | null;
  selected: VersionLite;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const ordered = [...versions].sort((a, b) => b.number - a.number);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
      >
        v{selected.number}
        {selected.status !== 'READY' && <StatusBadge status={selected.status} />}
        <span className="text-neutral-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-8 z-50 w-48 overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
            {ordered.map((v) => (
              <div
                key={v.id}
                className={`group/vrow flex items-center ${
                  v.id === selectedId ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelect(v.id);
                    setOpen(false);
                  }}
                  className={`flex flex-1 items-center justify-between gap-2 px-3 py-1.5 text-left text-xs ${
                    v.id === selectedId ? 'font-medium text-neutral-900' : 'text-neutral-600'
                  }`}
                >
                  <span>Version {v.number}</span>
                  {v.status !== 'READY' && <StatusBadge status={v.status} />}
                </button>
                {onDelete &&
                  (confirmId === v.id ? (
                    <div className="mr-1 flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(v.id);
                          setConfirmId(null);
                          setOpen(false);
                        }}
                        className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                        title="Confirm delete"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmId(null);
                        }}
                        className="rounded px-1 text-[11px] text-neutral-400 hover:text-neutral-700"
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmId(v.id);
                      }}
                      title="Delete this version"
                      className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-300 opacity-100 hover:bg-neutral-200 hover:text-red-600 md:opacity-0 md:group-hover/vrow:opacity-100"
                    >
                      ✕
                    </button>
                  ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The section (PDF) name in the header — click to rename inline (if editable). */
function ProofName({ proofId, name, editable }: { proofId: string; name: string; editable: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const rename = useMutation({
    mutationFn: () => renameProof(proofId, value.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proof', proofId] });
      setEditing(false);
    },
  });

  if (!editable) {
    return <span className="min-w-0 truncate font-semibold tracking-tight text-neutral-900">{name}</span>;
  }
  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) rename.mutate();
        }}
      >
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={200}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setValue(name);
              setEditing(false);
            }
          }}
          onBlur={() => {
            if (value.trim() && value.trim() !== name) rename.mutate();
            else setEditing(false);
          }}
          disabled={rename.isPending}
          className="w-full max-w-[14rem] rounded-md border border-neutral-300 px-2 py-0.5 text-sm font-semibold outline-none focus:border-neutral-900 sm:w-56"
        />
      </form>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setValue(name);
        setEditing(true);
      }}
      title="Rename PDF"
      className="group flex min-w-0 items-center gap-1 truncate text-left font-semibold tracking-tight text-neutral-900"
    >
      <span className="truncate">{name}</span>
      <span className="text-xs text-neutral-300 group-hover:text-neutral-500">✎</span>
    </button>
  );
}
